import {
  DndContext as DragAndDropContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Active,
  type DragOverEvent,
  type Over,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { functionalUpdate } from '@tanstack/react-table';
import { type VirtualItem } from '@tanstack/react-virtual';
import clsx from 'clsx';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type JSX,
  type MutableRefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import { DragAllDirectionIcon } from '@dynatrace/strato-icons';

import { getRowOrderRowModel } from './row-order-model.js';
import type { DataTableRowOrderProps } from './row-order-types.js';
import {
  createRestrictToTableRowAreaModifier,
  createRowOrderCollisionDetection,
  getAllRowsInTableBody,
  getDragHandleForRow,
  getDragIndexes,
  resetAllStylesForRows,
  transformRowOnDragOver,
} from './row-order-utils.js';
import * as styles from './RowOrder.sty.js';
import { Button } from '../../../../buttons/button/Button.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useModality } from '../../../../core/hooks/useModality.js';
import { focusRingStatic } from '../../../../core/styles/focusRingStatic.sty.js';
import { getProperInertValue } from '../../../../core/utils/get-inert-value.js';
import { devLog } from '../../../../core/utils/logging.js';
import { Grid } from '../../../../layouts/grid/Grid.js';
import { DataTableDefaultCell } from '../../components/DataTableDefaultCell.js';
import { DataTableRow } from '../../components/DataTableRow.js';
import {
  DATA_TABLE_BUILTIN_COLUMN_MIN_WIDTH,
  DATA_TABLE_ROW_DRAG_COLUMN_ID,
  DATA_TABLE_ROW_HEIGHT_MAP,
} from '../../constants.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import type {
  Row,
  Table,
  TableOptions,
  TableFeature,
  ColumnDef,
} from '../../hooks/useTable/types.js';
import { assignRowMethods } from '../../hooks/useTable/utils.js';
import { dataTableMessages } from '../../messages.js';
import type { DataTableRowData } from '../../public.api.js';
import { RowDetailsElement } from '../RowDetails/RowDetailsElement.js';
import type { DataTableThresholdsNormalizedRule } from '../Thresholds/threshold-types.js';
import * as thresholdStyles from '../Thresholds/Thresholds.sty.js';

// Threshold for detecting sudden jumps caused by virtualizer scroll corrections
// vs. gradual user/auto-scroll during drag.
const SCROLL_JUMP_THRESHOLD = DATA_TABLE_ROW_HEIGHT_MAP.default * 10;

/**
 * Checks if the `rowOrdering` prop is a configuration object.
 * @param rowOrdering - The table prop that enables row ordering.
 * @returns `true` if the param is an object, `false` otherwise.
 */
function isRowOrderConfigObject<TData>(
  rowOrdering:
    | boolean
    | {
        enableDragAndDrop?: boolean;
        disableRowDragAndDrop?: (row: TData) => boolean;
      },
): rowOrdering is {
  enableDragAndDrop?: boolean;
  disableRowDragAndDrop?: (row: TData) => boolean;
} {
  return typeof rowOrdering === 'object';
}

/**
 * Configuration hook for the row order in the DataTable.
 * @internal
 */
export function useRowOrder<TData extends DataTableRowData>(
  props: DataTableRowOrderProps<TData>,
  options: TableOptions<TData>,
): void {
  const { rowOrdering } = props;

  const disableRowDragAndDropFn =
    typeof rowOrdering === 'object'
      ? rowOrdering.disableRowDragAndDrop
      : undefined;

  const enableRowDragAndDropCallback = useCallback(
    (row: Row<TData>) => {
      // If no disable function is provided, enable drag and drop for all rows
      if (!disableRowDragAndDropFn) {
        return true;
      }
      return !disableRowDragAndDropFn(row.original);
    },
    [disableRowDragAndDropFn],
  );

  const [rowOrder, onRowOrderChange] = useControllableState<string[]>({
    value: props.rowOrder,
    defaultValue: props.defaultRowOrder,
    onChange: props.onRowOrderChange,
  });

  options.enableRowOrderDragAndDrop = false;
  if (rowOrdering === false || rowOrdering === undefined) {
    options.enableRowOrdering = false;
    return;
  }

  options.enableRowOrdering = true;
  options.getRowOrderRowModel = getRowOrderRowModel();
  options.onRowOrderChange = onRowOrderChange;
  options.state = { ...options.state, rowOrder };

  // config object is given
  if (isRowOrderConfigObject(rowOrdering)) {
    if (
      rowOrdering.enableDragAndDrop === true &&
      rowOrdering.disableRowDragAndDrop !== undefined
    ) {
      options.lockDisabledRows = rowOrdering.lockDisabledRows;
      options.enableRowOrderDragAndDrop = enableRowDragAndDropCallback;
      options.autoResetPageIndex = false;
    } else {
      options.enableRowOrderDragAndDrop =
        rowOrdering.enableDragAndDrop === true;
    }
  }
}

const RowDragHandleCell = ({
  rowId,
  disabled,
  isDraggingActive,
}: {
  rowId: string;
  disabled: boolean;
  isDraggingActive: boolean;
}): JSX.Element => {
  const intl = useIntl();
  const modality = useModality();
  const { attributes, listeners } = useSortable({
    id: rowId,
  });

  const isKeyboardDragging = isDraggingActive && modality === 'keyboard';
  const rowDragHandleLabel = intl.formatMessage(
    dataTableMessages.rowDragAndDropLabel,
    { rowId },
  );

  return (
    <DataTableDefaultCell>
      <Button
        size="condensed"
        variant="default"
        {...attributes}
        {...(!disabled ? listeners : undefined)}
        aria-disabled={disabled}
        aria-label={rowDragHandleLabel}
        className={clsx(
          styles.dndHandle({
            disabled,
          }),
          // Apply the focus ring unconditionally (when: 'always')
          // to the dragged row's handle during keyboard dragging.
          isKeyboardDragging &&
            focusRingStatic({
              minimal: false,
              color: 'neutral',
              when: 'always',
            }),
        )}
        data-dt-name="RowOrderDragAndDrop"
      >
        <Button.Prefix>
          <DragAllDirectionIcon />
        </Button.Prefix>
      </Button>
    </DataTableDefaultCell>
  );
};

/**
 * Column definition for the row drag column.
 * @internal
 */
export const ROW_DRAG_COLUMN: ColumnDef<unknown, unknown> = {
  id: DATA_TABLE_ROW_DRAG_COLUMN_ID,
  alignment: 'center',
  accessorFn: () => '',
  header: () => {
    return '';
  },
  width: 'content',
  minWidth: DATA_TABLE_BUILTIN_COLUMN_MIN_WIDTH,
  enableSorting: false,
  enableResizing: false,
  enableUserActions: false,
  enableHiding: false,
  meta: {
    builtin: true,
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
    builtinPlacement: 'prefix',
    cellDeps: (cell) => {
      return [cell.row.getCanDrag()];
    },
  },
  cell: ({ row, table }) => (
    <RowDragHandleCell
      rowId={row.id}
      disabled={!row.getCanDrag()}
      isDraggingActive={table.getState().draggingRowInfo?.id === row.id}
    />
  ),
};

/**
 * Defines the row order table feature.
 */
export const DataTableRowOrder: TableFeature = {
  createTable<TData>(table: Table<TData>) {
    /**
     * Allows access to the row model which comes before the row order.
     */
    table.getPreRowOrderRowModel = () => table.getExpandedRowModel();

    table.setDraggingRowInfo = (draggingRowInfo?: {
      id: string;
      index: number;
      row: Row<TData>;
    }) => {
      table.setState((state) => ({
        ...state,
        draggingRowInfo,
      }));
    };

    table.getRowOrderRowModel = () => {
      if (!table._getRowOrderRowModel && table.options.getRowOrderRowModel) {
        table._getRowOrderRowModel = table.options.getRowOrderRowModel(table);
      }

      if (!table._getRowOrderRowModel) {
        return table.getPreRowOrderRowModel();
      }

      return table._getRowOrderRowModel();
    };

    table.setRowOrder = (updater) => {
      const newValue = functionalUpdate(
        updater,
        table.getState().rowOrder ?? [],
      );

      table._queue(() => {
        table.options.onRowOrderChange?.(newValue);
      });
    };

    assignRowMethods(table, {
      getCanDrag(this: Row<TData>) {
        const { enableRowOrdering, enableRowOrderDragAndDrop } = table.options;
        const sortingState = table.getState().sorting;

        if (
          enableRowOrdering !== true ||
          // always disable the drag and drop handle if the rows are sorted
          (sortingState !== undefined && sortingState.length > 0)
        ) {
          return false;
        }

        // check if drag and drop is disabled for the row
        if (
          enableRowOrderDragAndDrop !== undefined &&
          typeof enableRowOrderDragAndDrop === 'function'
        ) {
          return enableRowOrderDragAndDrop(this);
        }
        return true;
      },

      disabledRowDragAndDropLockPosition:
        table.options.lockDisabledRows ?? false,
    });
  },
};

/**
 * @internal
 */
export function RowOrderingDragAndDropContext<TData extends DataTableRowData>({
  children,
  rows,
  virtualColumns,
  rowSeparation,
}: Readonly<{
  children: ReactNode;
  rows: Row<TData>[];
  virtualColumns: VirtualItem[];
  rowSeparation: 'none' | 'horizontalDividers' | 'zebraStripes';
}>): JSX.Element | undefined {
  const table = useDataTable();
  const modality = useModality();
  const tableHeaderHeight = table.getTotalHeaderHeight();
  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );
  const [draggingElement, setDraggingElement] = useState<ReactNode | undefined>(
    undefined,
  );
  const containedVariant = table.getVisualVariantOptions().contained;
  const virtualizationContainer = table.getRowVirtualizationContainer();
  const draggingRowInfo = table.getState().draggingRowInfo;

  const tableGridElement =
    virtualizationContainer?.querySelector<HTMLElement>('[role=grid]');

  const overlayContainer = useRef<HTMLDivElement>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);

  const rowOrderCollisionDetection = useMemo(
    () => createRowOrderCollisionDetection(draggingRowInfo),
    [draggingRowInfo],
  );

  const restrictToTableRowArea = useMemo(
    () =>
      createRestrictToTableRowAreaModifier(
        tableHeaderHeight,
        containedVariant,
        virtualizationContainer,
      ),
    [tableHeaderHeight, virtualizationContainer, containedVariant],
  );

  const rowOrderWhileDragging = useRef<string[]>([]);

  if (!tableGridElement || !virtualizationContainer) {
    return;
  }

  const overlayWidth = tableGridElement.parentElement?.clientWidth ?? 0;
  const originalRowOrder =
    table.getState().rowOrder ?? rows.map((row) => row.id);

  const resetDragging = (): void => {
    // Remove scroll event listener
    if (virtualizationContainer && scrollHandlerRef.current) {
      virtualizationContainer.removeEventListener(
        'scroll',
        scrollHandlerRef.current,
      );
      scrollHandlerRef.current = null;
    }

    table.setDraggingRowInfo(undefined);
    setDraggingElement(undefined);
    resetAllStylesForRows(getAllRowsInTableBody(tableGridElement));
  };

  const handleOnDragStart = ({ active }: { active: Active }): void => {
    if (active.data.current) {
      const element = createDraggingElement<TData>(
        overlayContainer,
        active,
        rows,
        virtualColumns,
        {
          width: overlayWidth,
          gridTemplateColumns: table.getGridTemplateColumns(),
          rowSeparation,
          hasThresholds: table.options.hasThresholds,
          rowThresholds: table.options.rowThresholds,
          rowDetailsTemplate: table.options.rowDetailsTemplate,
        },
      );
      setDraggingElement(element);

      // Use requestAnimationFrame to delay execution until just before the next paint,
      // ensuring the DOM is fully updated. This allows us to sync the overlay's scroll
      // position with the table grid's scroll state.
      requestAnimationFrame(() => {
        if (overlayContainer.current?.parentElement) {
          overlayContainer.current.parentElement.scrollLeft =
            tableGridElement?.parentElement?.scrollLeft ?? 0;
        }
      });

      table.setDraggingRowInfo({
        id: active.id as string,
        index: active.data.current.rowIndex,
        row: active.data.current.row,
      });

      rowOrderWhileDragging.current = originalRowOrder
        ? [...originalRowOrder]
        : [];

      let lastScrollOffset = virtualizationContainer?.scrollTop ?? 0;

      // Attach scroll event listener to handle virtualization during drag
      const handleScroll = (): void => {
        if (!virtualizationContainer) {
          return;
        }

        const currentOffset = virtualizationContainer.scrollTop;
        const scrollDelta = Math.abs(currentOffset - lastScrollOffset);
        if (scrollDelta >= SCROLL_JUMP_THRESHOLD) {
          // Large sudden jump - virtualizer correction, prevent it
          virtualizationContainer.scrollTop = lastScrollOffset;
          return;
        }
        lastScrollOffset = currentOffset;
      };
      scrollHandlerRef.current = handleScroll;
      virtualizationContainer?.addEventListener('scroll', handleScroll);
    }
  };

  const handleOnDragEnd = ({
    active,
    over,
  }: {
    active: Active;
    over: Over | null;
  }): void => {
    if (over === undefined || over === null) {
      devLog('[Table] On drag end: over info is undefined or null', over);
      resetDragging();
      return;
    }

    if (draggingRowInfo === undefined) {
      devLog(
        '[Table] On drag end: dragging row info is undefined',
        draggingRowInfo,
      );
      resetDragging();
      return;
    }

    // if dragged to the starting point, row order does not change
    if (over.id === active.id) {
      resetDragging();
      return;
    }

    const { fromIndex, toIndex } = getDragIndexes(
      originalRowOrder,
      active.id as string, // for us the IDs are always strings
      over.id as string, // for us the IDs are always strings
    );

    const newRowOrder = [...originalRowOrder];
    const activeRowSubRowsCount = draggingRowInfo.row.getLeafRows().length;
    const overRowSubRowsCount = (
      over.data.current?.row as Row<unknown>
    ).getLeafRows().length;

    const rowsToMove = newRowOrder.splice(fromIndex, 1 + activeRowSubRowsCount);

    newRowOrder.splice(
      fromIndex < toIndex
        ? toIndex + overRowSubRowsCount - activeRowSubRowsCount
        : toIndex,
      0,
      ...rowsToMove,
    );
    table.setRowOrder(newRowOrder);
    resetDragging();

    // In Safari, after scrolling a table while dragging, the cells are weirdly shifted in the grid
    // We did not find why, probably a safari bug. This fixes the issue for now.
    virtualizationContainer?.scrollBy({ top: 1, behavior: 'smooth' });
    virtualizationContainer?.scrollBy({ top: -1, behavior: 'smooth' });
    // Always move focus to the dropped row's drag handle so the user can
    // continue navigating from the new position (e.g. via Tab).
    // For mouse/touch we pass `focusVisible: false` to suppress the focus ring.
    requestAnimationFrame(() => {
      const dragHandle = getDragHandleForRow(tableGridElement, toIndex);
      dragHandle?.focus(
        modality !== 'keyboard' ? { focusVisible: false } : undefined,
      );
    });
  };

  return (
    <DragAndDropContext
      sensors={sensors}
      collisionDetection={rowOrderCollisionDetection}
      modifiers={[restrictToTableRowArea]}
      onDragStart={handleOnDragStart}
      onDragEnd={handleOnDragEnd}
      onDragOver={(event: DragOverEvent) =>
        transformRowOnDragOver(
          tableGridElement,
          event,
          originalRowOrder,
          table.getVisualVariantOptions().rowSeparation === 'none',
          draggingRowInfo?.row,
        )
      }
      onDragCancel={() => resetDragging()}
    >
      {children}
      {
        // We need to portal the drag and drop overlay to escape CSS transforms on ancestor elements.
        // This ensures transforms on parent containers don't affect the position of the drag overlay.
        createPortal(
          <DragOverlay
            dropAnimation={null}
            className={styles.rowDndOverlay}
            style={{ width: overlayWidth, height: 'max-content' }}
          >
            {draggingElement}
          </DragOverlay>,
          document.body,
        )
      }
    </DragAndDropContext>
  );
}

/**
 * Creates element to place in drag overlay.
 * @param overlayContainer - The container element that contains the row being dragged.
 * @param active - The row being dragged. If no corresponding row is found,
 *                 then undefined is returned from this function.
 * @param rows - all rows.
 * @param virtualColumns - virtual items for the columns
 * @param rowConfig - object containing all necessary row configurations to be able to replicate the row for the drag overlay
 * @returns element intended for overlay
 */
function createDraggingElement<TData extends DataTableRowData>(
  overlayContainer: MutableRefObject<HTMLDivElement | null>,
  active: Active,
  rows: Row<TData>[],
  virtualColumns: VirtualItem[],
  rowConfig: {
    width: number;
    gridTemplateColumns: string;
    rowSeparation: 'none' | 'horizontalDividers' | 'zebraStripes';
    hasThresholds?: boolean;
    rowThresholds?: DataTableThresholdsNormalizedRule<unknown>[];
    rowDetailsTemplate?: (params: { row: TData }) => ReactNode;
  },
): JSX.Element | undefined {
  const activeId = active.id as string;
  const draggingOriginalRow = active.data.current?.row as Row<TData>;
  const draggingVirtualRow = active.data.current?.virtualRow as VirtualItem;

  const {
    gridTemplateColumns,
    rowSeparation,
    hasThresholds,
    rowThresholds,
    rowDetailsTemplate,
  } = rowConfig;

  if (draggingOriginalRow === undefined || draggingVirtualRow === undefined) {
    devLog(
      `[DataTable] Dragging row or virtual row with row id ${activeId} is undefined.`,
    );
    return;
  }

  const isRowDetailsOpen = draggingOriginalRow.getIsDetailsOpen();

  const draggingVirtualDetailsRow = {
    ...draggingVirtualRow,
    index: draggingVirtualRow.index + 1,
  };

  return (
    <Grid
      ref={overlayContainer}
      gridTemplateColumns={gridTemplateColumns}
      padding={0}
      minWidth="max-content"
      gap={0}
      // TODO: React 19 update - inert is then in the IntrinsicAttributes and can be passed directly
      {...{ inert: getProperInertValue(true) }}
      className={clsx({
        [thresholdStyles.hasPillThresholds]:
          hasThresholds &&
          rowThresholds?.some((threshold) => threshold.type === 'pill'),
      })}
      style={{
        position: 'relative',
      }}
    >
      <DataTableRow
        row={draggingOriginalRow}
        virtualRow={draggingVirtualRow}
        virtualItemIndex={0}
        rowCount={rows.length}
        virtualColumns={virtualColumns}
        isDraggedRow={false}
        isInDragOverlay={true}
        rowParity="even"
      />
      {isRowDetailsOpen ? (
        <RowDetailsElement
          originalRow={draggingOriginalRow}
          virtualRow={draggingVirtualDetailsRow}
          virtualItemIndex={1}
          rowSeparation={rowSeparation}
          rowParity="even"
        >
          {rowDetailsTemplate?.({
            row: draggingOriginalRow.original,
          })}
        </RowDetailsElement>
      ) : null}
    </Grid>
  );
}
